import { ConsentTrackingStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Form,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { FormEventHandler } from "react"
import {
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react"

import { AccountInformationPage } from "./AccountInformationPage"
import { MissingInfoPage } from "./MissingInfoPage"
import { OptionalConsentsPage } from "./OptionalConsentsPage"
import { PasswordPage } from "./PasswordPage"
import { PersonalInformationPage } from "./PersonalInformationPage"
import { RequiredConsentsPage } from "./RequiredConsentsPage"
import styles from "./ReturningUser.module.scss"
import {
  PublicPageHeader,
  OverlayLoader,
  NoticeOfPrivacyPracticeForm,
} from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useAccount,
  useConsents,
  useSources,
  useUpdateConsents,
  usePendingConsents,
} from "src/hooks"
import { GenericErrorToast } from "src/toasts"
import { passwordPattern } from "src/utils"

const polyglotPrefix = "pages.update_account."

const missingInfoPages = Object.freeze([
  MissingInfoPage.NoticeOfPrivacyPractice,
  MissingInfoPage.RequiredConsentNotices,
  MissingInfoPage.OptionalConsentNotices,
  MissingInfoPage.PersonalInformation,
  MissingInfoPage.AccountInformation,
  MissingInfoPage.Password,
])

interface Props {
  currentPassword: string;
  onComplete: () => Promise<void>;
}

interface ConsentForm {
  nppConsent: ConsentTrackingStatus;
}

// replace occurrences of ConsentTrackingStatus.Unacknowledged with ConsentTrackingStatus.Declined
function acceptedOrDeclined(consentStatus: ConsentTrackingStatus) {
  if (consentStatus === ConsentTrackingStatus.Accepted) {
    return consentStatus
  }

  return ConsentTrackingStatus.Declined
}

export function MissingInfoFlow(props: Props) {
  const {
    onComplete,
    currentPassword,
  } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const {
    data: account,
    isLoading: isLoadingAccount,
  } = useAccount()

  const {
    data: sources,
    isLoading: isLoadingSources,
  } = useSources()

  const {
    data: consents,
    isLoading: isLoadingConsents,
  } = useConsents()
  const updateConsents = useUpdateConsents()

  const {
    data: pendingConsents,
    isLoading: isLoadingPendingConsents,
  } = usePendingConsents()

  const initialConsentForm: ConsentForm = useMemo(
    () => {
      if (!consents) {
        return {
          // default to declined for all required consent pages
          nppConsent: ConsentTrackingStatus.Declined,
        }
      }

      return {
        nppConsent: acceptedOrDeclined(consents.npp_consent.status),
      }
    },
    [consents],
  )

  const [
    consentForm,
    setConsentForm,
  ] = useState<ConsentForm>(initialConsentForm)

  const updateConsentForm = useCallback(
    (value: Partial<ConsentForm>) => {
      setConsentForm({
        ...consentForm,
        ...value,
      })
    },
    [
      consentForm,
      setConsentForm,
    ],
  )

  const pagesRef = useRef<MissingInfoPage[]>([])

  const isLoading = (
    isLoadingConsents ||
    consents === undefined ||
    isLoadingPendingConsents ||
    pendingConsents === undefined ||
    isLoadingAccount ||
    account === undefined ||
    isLoadingSources ||
    sources === undefined
  )

  const pages: MissingInfoPage[] = useMemo(
    () => {
      // don't define the missing info pages more than once
      if (pagesRef.current.length > 0) {
        return pagesRef.current
      }

      // don't define the pages until relevant info has loaded
      if (isLoading) {
        return []
      }

      const passwordRegExp = new RegExp(passwordPattern)
      const hasValidPassword = passwordRegExp.test(currentPassword)

      const filteredPages = missingInfoPages.filter((page) => {
        if (page === MissingInfoPage.Password) {
          return !hasValidPassword
        }

        if (page === MissingInfoPage.NoticeOfPrivacyPractice) {
          // no need to complete npp_consent if the user has no data source
          if (!sources?.length) {
            return false
          }

          const nppStatus = consents?.npp_consent.status

          // show NPP consent if the user has not acknowledged it yet
          return !nppStatus || (
            nppStatus !== ConsentTrackingStatus.Accepted && nppStatus !== ConsentTrackingStatus.Declined
          )
        }

        if (
          page === MissingInfoPage.PersonalInformation ||
        page === MissingInfoPage.AccountInformation
        ) {
          return !account?.birthdate || !account?.country
        }

        if (page === MissingInfoPage.RequiredConsentNotices) {
          return (
            pendingConsents?.required?.consents &&
          pendingConsents?.required?.consents.length > 0
          )
        }

        if (page === MissingInfoPage.OptionalConsentNotices) {
          return (
            pendingConsents?.optional?.consents &&
          pendingConsents?.optional?.consents.length > 0
          )
        }

        return true
      })

      pagesRef.current = filteredPages
      return filteredPages
    },
    [
      isLoading,
      currentPassword,
      account,
      pendingConsents,
      consents,
      sources,
    ],
  )

  const [
    currentPageIndex,
    setCurrentPageIndex,
  ] = useState<number>(0)

  const currentPage = pages[currentPageIndex]

  const previousPageIndex: number | undefined = useMemo(
    () => {
      if (currentPageIndex <= 0) {
        return undefined
      }

      return currentPageIndex - 1
    },
    [currentPageIndex],
  )

  const nextPageIndex: number | undefined = useMemo(
    () => {
      if (currentPageIndex >= pages.length - 1) {
        return undefined
      }

      return currentPageIndex + 1
    },
    [
      pages,
      currentPageIndex,
    ],
  )

  const onBackClick = useCallback(
    () => {
      if (previousPageIndex !== undefined) {
        setCurrentPageIndex(previousPageIndex)
      }
    },
    [
      previousPageIndex,
      setCurrentPageIndex,
    ],
  )

  const completeMissingInfo = useCallback(
    async () => {
      setIsOverlayLoaderVisible(true)

      try {
        if (pages.includes(MissingInfoPage.NoticeOfPrivacyPractice)) {
          if (!updateConsents) {
            throw new Error("cannot update consents")
          }

          await updateConsents({
            npp_consent: {
              status: consentForm.nppConsent,
            },
          })
        }

        await onComplete()
      } catch {
        showToast(
          "edit-consents-error-toast",
          GenericErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      showToast,
      updateConsents,
      consentForm,
      onComplete,
      pages,
      setIsOverlayLoaderVisible,
    ],
  )

  const proceedToNextStep = useCallback(
    () => {
      if (nextPageIndex !== undefined) {
        setCurrentPageIndex(nextPageIndex)
      } else {
        void completeMissingInfo()
      }
    },
    [
      nextPageIndex,
      setCurrentPageIndex,
      completeMissingInfo,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      proceedToNextStep()
    },
    [proceedToNextStep],
  )

  const submitButton = (
    <ActionSubmit
      className={styles.submit}
      value={polyglot.t(
        `${polyglotPrefix}${nextPageIndex === undefined ? "submit_cta" : "next_cta"}`,
      )}
      id="submit-button"
    />
  )

  return (
    <div className={styles.container}>
      <PublicPageHeader onBackClick={previousPageIndex === undefined ? undefined : onBackClick}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </PublicPageHeader>
      {currentPage === undefined && (
        isLoading ? (
          <OverlayLoader visible />
        ) : (
          <Form
            className={styles.emptyForm}
            onSubmit={onSubmit}
          >
            <div className={styles.emptyMessage}>
              {polyglot.t(`${polyglotPrefix}empty_message`)}
            </div>
            <ActionSubmit
              className={styles.submit}
              value={polyglot.t(`${polyglotPrefix}empty_cta`)}
              id="submit-button"
            />
          </Form>
        )
      )}
      {currentPage === MissingInfoPage.NoticeOfPrivacyPractice && (
        <NoticeOfPrivacyPracticeForm
          form={consentForm}
          updateForm={updateConsentForm}
          onSubmit={onSubmit}
        >
          {submitButton}
        </NoticeOfPrivacyPracticeForm>
      )}
      {currentPage === MissingInfoPage.PersonalInformation && (
        <PersonalInformationPage onSubmitted={proceedToNextStep}>
          {submitButton}
        </PersonalInformationPage>
      )}
      {currentPage === MissingInfoPage.AccountInformation && (
        <AccountInformationPage onSubmitted={proceedToNextStep}>
          {submitButton}
        </AccountInformationPage>
      )}
      {currentPage === MissingInfoPage.Password && (
        <PasswordPage
          currentPassword={currentPassword}
          onSubmitted={proceedToNextStep}
        >
          {submitButton}
        </PasswordPage>
      )}
      {currentPage === MissingInfoPage.RequiredConsentNotices && (
        <RequiredConsentsPage onSubmitted={proceedToNextStep}>
          {submitButton}
        </RequiredConsentsPage>
      )}
      {currentPage === MissingInfoPage.OptionalConsentNotices && (
        <OptionalConsentsPage onSubmitted={proceedToNextStep}>
          {submitButton}
        </OptionalConsentsPage>
      )}
    </div>
  )
}
